import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../services/supabaseClient'; 
import { useAuth } from '../../hooks/useAuth';
import { useCarouselScroll } from '../../hooks/useCarouselScroll';
import { dashboardStyles as s } from '../../styles/dashboardStyles';
import { tutorialsStyles as ts } from '../../styles/tutorialsStyles';

// Colores del loop según tu tailwind.config.js
const COLOR_LOOP = [
  'bg-msc-yellow',
  'bg-msc-blue',
  'bg-msc-red',
  'bg-msc-green'
];

const Tutorials = () => {
  const { loading: authLoading } = useAuth();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const scrollRef = useRef(null);
  
  const { canScrollLeft, canScrollRight, scroll } = useCarouselScroll(scrollRef, videos);

  useEffect(() => {
    let isMounted = true;
    const fetchVideos = async () => {
      if (authLoading) return;
      try {
        setFetchLoading(true);
        const { data, error } = await supabase.from('tutorials').select('title, youtube_id, duration').order('created_at', { ascending: true });
        if (error) throw error;
        if (isMounted) setVideos(data || []);
      } catch (err) {
        console.error(err.message);
      } finally {
        if (isMounted) setFetchLoading(false);
      }
    };
    fetchVideos();
    return () => { isMounted = false };
  }, [authLoading]);

  if (authLoading || fetchLoading) {
    return <div className="py-20 text-center text-slate-300 animate-pulse uppercase tracking-[0.2em] text-[10px] font-black italic">Sincronizando tutoriales...</div>;
  }

  return (
    <section id="tutorials" className={s.section}>
      <div className={s.headerContainer}>
        <h3 className={s.headerTitle}>Operativas y Tutoriales</h3>
        <p className={s.headerSubtitle}>Capacitación por Video</p>
      </div>
      
      <div className={ts.carouselWrapper}> 
        <button onClick={() => scroll('left')} className={ts.navButton('left', canScrollLeft)}>
          <div className={ts.navIconBox}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </button>

        <div ref={scrollRef} className={ts.gallery}>
          {videos.map((v, index) => {
            const id = v.youtube_id.split(/[?&]/)[0];
            const colorClass = COLOR_LOOP[index % COLOR_LOOP.length];
            
            return (
              <div 
                key={v.youtube_id} 
                className={ts.videoCard} 
                style={{ animationDelay: `${index * 150}ms` }}
                onClick={() => setSelectedVideo({id, title: v.title})}
              >
                <div className={ts.thumbContainer}>
                  {/* Tag estilo News para duración */}
                  {v.duration && (
                    <div className={ts.tagContainer}>
                      <span className={ts.tag}>{v.duration}</span>
                    </div>
                  )}

                  <img 
                    src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`} 
                    alt={v.title}
                    className={ts.image}
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`; }}
                  />
                  
                  <div className={ts.playOverlay}>
                    <div className={ts.playCircle}>
                      <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className={ts.titleContainer}> 
                  <h4 className={ts.videoTitle}>{v.title}</h4>
                  <div className={ts.titleUnderline(colorClass)} />
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={() => scroll('right')} className={ts.navButton('right', canScrollRight)}>
          <div className={ts.navIconBox}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      {selectedVideo && createPortal(
        <div className={ts.modalOverlay} onClick={() => setSelectedVideo(null)}>
          <button className={ts.modalClose} onClick={() => setSelectedVideo(null)}>✕</button>
          <div className={ts.modalContent} onClick={e => e.stopPropagation()}>
            <iframe 
              className="w-full h-full" 
              src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&rel=0&modestbranding=1`} 
              title={selectedVideo.title}
              frameBorder="0" 
              allow="autoplay; encrypted-media"
              allowFullScreen 
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Tutorials;